// import node module libraries
import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import axios from "axios";
import toast from "react-hot-toast";
import { TableData } from "@/types";
import _ from "lodash";

// Single source for backend API base URL – bind all APIs to this (env or default)
const API_BASE_URL =
  (typeof process !== "undefined" && process.env?.NEXT_PUBLIC_API_BASE_URL) ||
  "https://api.alfasage.anervea.ai/";
const baseUrl = API_BASE_URL.endsWith("/") ? API_BASE_URL : `${API_BASE_URL}/`;

export const BASE_URL = baseUrl;

/** Use in client components when calling the backend directly */
export function getApiBaseUrl(): string {
  return baseUrl;
}

export const axiosInstance = axios.create({
  baseURL: baseUrl,
  timeout: 600000,
});

const isDev =
  process.env.NODE_ENV === "development" ||
  (typeof window !== "undefined" && (window as any).__DEV__);

// Log request payload and response in development so they are visible (browser console or server log)
if (isDev) {
  axiosInstance.interceptors.request.use((config) => {
    console.log("[API Request]", config.method?.toUpperCase(), config.url, config.data);
    return config;
  });
  axiosInstance.interceptors.response.use(
    (response) => {
      console.log("[API Response]", response.config.url, response.status, response.data);
      return response;
    },
    (error) => {
      console.log(
        "[API Error]",
        error.config?.url,
        error.response?.status,
        error.response?.data
      );
      return Promise.reject(error);
    }
  );
} else {
  axiosInstance.interceptors.response.use(
    (response) => response,
    (error) => Promise.reject(error)
  );
}

// In browser: log client-side API calls (to /api/*) so payload and response are visible in DevTools
if (typeof window !== "undefined" && process.env.NODE_ENV === "development") {
  axios.interceptors.request.use((config) => {
    if (config.url?.includes("/api")) {
      console.log("[Network Request]", config.method?.toUpperCase(), config.url, config.data);
    }
    return config;
  });
  axios.interceptors.response.use(
    (response) => {
      if (response.config.url?.includes("/api")) {
        console.log("[Network Response]", response.config.url, response.status, response.data);
      }
      return response;
    },
    (error) => {
      if (error.config?.url?.includes("/api")) {
        console.log("[Network Error]", error.config?.url, error.response?.status, error.response?.data);
      }
      return Promise.reject(error);
    }
  );
}

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// Format file name function
export function formatFileName(inputText: string) {
  if (inputText) {
    // Remove file extension if present (e.g., ".pdf")
    let formattedText = inputText?.replace(/\.[^.\/\\]*$/, "");

    // Replace hyphens and underscores with spaces
    formattedText = formattedText.replace(/[-_]/g, " ");

    // Capitalize each word
    formattedText = formattedText.replace(/\b\w/g, function (char) {
      return char.toUpperCase();
    });

    return formattedText;
  }
}

export function maskPhoneNumber(number: string) {
  if (number.length === 10) {
    return `${number.slice(0, 2)}******${number.slice(8)}`;
  }
  return number;
}

export function tableToCSV(data: any) {
  const csvRows = [];
  const headers = Object.keys(data[0]);
  csvRows.push(headers.join(","));

  // Format the data
  for (const row of data) {
    const values = headers.map((header) => {
      const escaped = ("" + row[header]).replace(/"/g, '\\"');
      return `"${escaped}"`;
    });
    csvRows.push(values.join(","));
  }

  return csvRows.join("\n");
}

// Sample chart color palette
export const colorPalette: string[] = ["#3BBDC4", "#510094", "#162550"];

// Generating color if color limit exceeds
const interpolateColor = (
  color1: string,
  color2: string,
  factor: number
): string => {
  const r1 = parseInt(color1.slice(1, 3), 16);
  const g1 = parseInt(color1.slice(3, 5), 16);
  const b1 = parseInt(color1.slice(5, 7), 16);

  const r2 = parseInt(color2.slice(1, 3), 16);
  const g2 = parseInt(color2.slice(3, 5), 16);
  const b2 = parseInt(color2.slice(5, 7), 16);

  const r = Math.round(r1 + factor * (r2 - r1));
  const g = Math.round(g1 + factor * (g2 - g1));
  const b = Math.round(b1 + factor * (b2 - b1));

  return `#${((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1)}`;
};

// Generating chart color based on color palette and interpolateColor
export const generateExtendedColors = (count: number): string[] => {
  const colors: string[] = [];
  const paletteLength = colorPalette.length;

  if (count <= paletteLength) {
    for (let i = 0; i < count; i++) {
      colors.push(colorPalette[i]);
    }
  } else {
    for (let i = 0; i < count; i++) {
      const color1 = colorPalette[i % paletteLength];
      const color2 = colorPalette[(i + 1) % paletteLength];
      const factor = (i % paletteLength) / (paletteLength - 1);
      colors.push(interpolateColor(color1, color2, factor));
    }
  }

  return colors;
};

export async function copyToClipboard(text: string) {
  try {
    await navigator.clipboard.writeText(text);
    toast.success("Text copied to clipboard", {
      position: "bottom-right",
    });
  } catch (err) {
    console.error("Failed to copy text to clipboard:", err);
  }
}

export function getYaxisData(chartData: any[], yAxis: string | string[]): any {
  // Case 1: If yAxis is a single string, return it directly.
  if (typeof yAxis === "string") {
    return yAxis;
  }

  // Case 2: If yAxis is an array, return the last item as a single string.
  if (Array.isArray(yAxis)) {
    return yAxis.length > 0 ? yAxis[yAxis.length - 1] : null;
  }
}

export function formatDate(dateString: string) {
  // Create a new Date object from the date string
  const date = new Date(dateString);

  // Define an array of month names
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  // Extract day, month, and year from the Date object
  const day = date.getUTCDate();
  const month = months[date.getUTCMonth()];
  const year = date.getUTCFullYear();

  // Function to add ordinal suffix to day
  function getOrdinalSuffix(day: number) {
    if (day > 3 && day < 21) return "th"; // Special case for 11-13
    switch (day % 10) {
      case 1:
        return "st";
      case 2:
        return "nd";
      case 3:
        return "rd";
      default:
        return "th";
    }
  }

  // Format day with ordinal suffix
  const dayWithSuffix = day + getOrdinalSuffix(day);

  // Return the formatted date string
  return `${dayWithSuffix} ${month} ${year}`;
}

// Example usage
const formattedDate = formatDate("2024-09-16T04:30:01.265Z");

export const wait = (ms: number) =>
  new Promise((resolve) => setTimeout(resolve, ms));

export function slugify(text: string) {
  return text
    .toString()
    .toLowerCase() // Convert to lowercase
    .trim() // Trim whitespace from both ends
    .replace(/\s+/g, "-") // Replace spaces with hyphens
    .replace(/[^\w\-]+/g, "") // Remove all non-word characters
    .replace(/\-\-+/g, "-") // Replace multiple hyphens with a single hyphen
    .replace(/^-+|-+$/g, ""); // Trim hyphens from the start and end
}

export function formatNumberWithCommas(number: number) {
  // if (typeof number !== "number") {
  //   throw new Error("Input must be a number");
  // }
  // return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  if (typeof number !== "number") {
    throw new Error("Input must be a number");
  }

  // Convert number to a string and split into whole and decimal parts
  const [wholePart, decimalPart] = number.toString().split(".");

  // Format the whole part with commas
  // const formattedWholePart = wholePart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

  const formattedWholePart = number
    .toFixed(0)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, ",");

  return formattedWholePart;

  // Combine whole part and decimal part if it exists
  // return decimalPart
  //   ? `${formattedWholePart}.${decimalPart}`
  //   : formattedWholePart;
}

export function getInitials(fullName: string | null | undefined) {
  if (!fullName) return "";
  // Split the full name by spaces
  const names = fullName.trim().split(" ");

  // Get the first letter of the first and last names
  const firstInitial = names[0] ? names[0][0].toUpperCase() : "";
  const lastInitial = names[names.length - 1]
    ? names[names.length - 1][0].toUpperCase()
    : "";

  // Return the initials as a string
  return `${firstInitial}${lastInitial}`;
}

export const delay = (ms: number) => {
  return new Promise((resolve) => setTimeout(resolve, ms));
};

export function formatString(str: string) {
  return str
    .toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

export function formatCapitalized(str: string) {
  return str
    .toLowerCase()
    .split(/[\s_]+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

export function removeUnderScore(str: string | undefined) {
  if (!str) return;
  return str.replace(/_/g, " ");
}

export const setCorsHeaders = (res: any) => {
  // Allow requests from any origin
  res.headers.set("Access-Control-Allow-Origin", "*");

  res.headers.set(
    "Access-Control-Allow-Methods",
    "GET, POST, OPTIONS, PUT, DELETE"
  );

  res.headers.set(
    "Access-Control-Allow-Headers",
    "Content-Type, Authorization"
  );

  // Allow framing from any origin
  res.headers.set("Content-Security-Policy", "frame-ancestors *");

  res.headers.set("Access-Control-Allow-Credentials", "true");
  return res;
};

export async function copyTableToClipboard(
  data: { description: string; tableData: { [key: string]: string }[] } | null
) {
  if (!data) return toast.error("No data available to copy.");

  const { description, tableData } = data;

  const headers = Object.keys(tableData[0]);
  const tableCsv = [headers.join(",")]
    .concat(
      tableData.map((row) => headers.map((header) => row[header]).join(","))
    )
    .join("\n");

  try {
    await navigator.clipboard.writeText(tableCsv);
  } catch (error) { }
}

export async function copyContentToClipboard(data: {
  description: string;
  tableData?: TableData[];
}) {
  if (!data) {
    toast.error("No data available to copy.");
    return;
  }

  const { description, tableData } = data;

  const headers = tableData ? Object.keys(tableData[0]) : [];
  const rows = tableData ? tableData : [];

  const htmlContent = `
    <div style="padding: 10px 0px;">
      <p>${description}</p>
      ${tableData
      ? `
        <table border="1" style="border-collapse: collapse; width: 100%;">
            <thead>
                <tr>${headers
        .map((header) => `<th>${formatString(header)}</th>`)
        .join("")}</tr>
            </thead>
            <tbody>${rows
        .map(
          (row) =>
            `<tr>${headers
              .map((header) => `<td>${row[header]}</td>`)
              .join("")}</tr>`
        )
        .join("")}</tbody>
        </table>
      `
      : ``
    }
    </div>
  `;

  try {
    const blob = new Blob([htmlContent], { type: "text/html" });
    await navigator.clipboard.write([new ClipboardItem({ "text/html": blob })]);
  } catch (error) { }
}

// export function getAggregateedData(
//   aggregateFunc: string,
//   yAxisKeyName: string,
//   data: TableData[]
// ): TableData[] | number {
//   if (!data || data.length === 0) return [];

//   const yAxisData = data
//     .map((item) => {
//       const value = item[yAxisKeyName];
//       return typeof value === "number" ? value : parseFloat(value as string);
//     })
//     .filter((value) => !isNaN(value));

//   switch (aggregateFunc) {
//     case "sum":
//       return sum(yAxisData);
//     case "count":
//       return yAxisData.length;
//     case "min":
//       return min(yAxisData);
//     case "max":
//       return max(yAxisData);
//     case "median":
//       return median(yAxisData);
//     case "mean":
//       return mean(yAxisData);
//     default:
//       return data;
//   }
// }

// type TableData = {
//   [key: string]: number | string;
// };

// export function aggregateTableData(
//   tableData: TableData[],
//   xAxis: string,
//   yAxis: string,
//   aggregationType: string
// ): TableData[] {
//   const aggregationTypeLower = aggregationType.toLowerCase().trim();

//   // Group by x_axis using lodash
//   const groupedData = _.groupBy(tableData, xAxis);

//   const result: TableData[] = [];

//   // Loop through grouped data and apply aggregation function
//   for (const groupKey in groupedData) {
//     const groupValues = groupedData[groupKey].map((item) => item[yAxis]);

//     let aggregatedValue: number;

//     switch (aggregationTypeLower) {
//       case "sum":
//         aggregatedValue = _.sum(groupValues as number[]);
//         break;
//       case "mean":
//         aggregatedValue = _.mean(groupValues as number[]);
//         break;
//       case "min":
//         aggregatedValue = _.min(groupValues as number[])!;
//         break;
//       case "max":
//         aggregatedValue = _.max(groupValues as number[])!;
//         break;
//       case "median":
//         aggregatedValue = median(groupValues as number[]);
//         break;
//       case "count":
//         aggregatedValue = groupValues.length;
//         break;
//       default:
//         return [];
//     }

//     result.push({
//       [xAxis]: groupKey,
//       [yAxis]: aggregatedValue,
//     });
//   }

//   return result;
// }

export function aggregateTableData<T extends TableData>(
  tableData: T[],
  xAxis: string,
  yAxis: string,
  aggregationType: string
): T[] {
  const aggregationTypeLower = aggregationType.toLowerCase().trim();

  const groupedData: Record<string, T[]> = {};

  tableData.forEach((item) => {
    const key = item[xAxis] as string;
    if (!groupedData[key]) {
      groupedData[key] = [];
    }
    groupedData[key].push(item);
  });

  const result: T[] = [];

  // Function to calculate the median
  const median = (values: number[]): number => {
    const sorted = [...values].sort((a, b) => a - b);
    const middle = Math.floor(sorted.length / 2);
    return sorted.length % 2 === 0
      ? (sorted[middle - 1] + sorted[middle]) / 2
      : sorted[middle];
  };

  // Loop through grouped data and apply aggregation function
  for (const groupKey in groupedData) {
    const groupValues = groupedData[groupKey].map(
      (item) => item[yAxis] as number
    );

    let aggregatedValue: number;

    switch (aggregationTypeLower) {
      case "sum":
        aggregatedValue = groupValues.reduce((sum, value) => sum + value, 0);
        break;
      case "mean":
        aggregatedValue =
          groupValues.reduce((sum, value) => sum + value, 0) /
          groupValues.length;
        break;
      case "min":
        aggregatedValue = Math.min(...groupValues);
        break;
      case "max":
        aggregatedValue = Math.max(...groupValues);
        break;
      case "median":
        aggregatedValue = median(groupValues);
        break;
      case "count":
        aggregatedValue = groupValues.length;
        break;
      default:
        return [];
    }

    result.push({
      [xAxis]: groupKey,
      [yAxis]: aggregatedValue,
    } as T); // Ensure the result matches the input type T
  }

  return result;
}
